/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    forwardRef
} from '@angular/core';
import { OptionComponent } from './select-option/select-option.component';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import {
    selectSearchInputStylesSAG,
    selectSearchInputStylesSHS,
    selectSearchStylesSAG,
    selectSearchStylesSHS,
    selectStylesSAG,
    selectStylesSHS
} from './select.styles';
import { componentStylesByTheme } from '../utils/theme.utils';
import { Subscription, tap } from 'rxjs';

@Component({
    selector: 'sag-ui-select',
    templateUrl: './select.component.html',
    styleUrls: [],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        }
    ]
})
export class SelectComponent
    implements AfterContentInit, ControlValueAccessor, Validator, OnDestroy, OnChanges, AfterViewInit
{
    @Input() id = this.randomId();

    /**
     * The placeholder of the Select.
     */
    @Input()
    placeholder = '';

    /**
     * Whether you can select multiple options.
     */
    @Input()
    multiple = false;

    /*
     * Whether the Select is required.
     */
    @Input()
    required = false;

    /**
     * Whether the Select is disabled.
     */
    @Input()
    disabled = false;

    /**
     * Whether the Select is read-only.
     */
    @Input()
    readOnly = false;

    /**
     * The label of the Select.
     */
    @Input()
    label = '';

    /**
     * Whether the switch is checked.
     */
    @Input()
    value: any | any[] = '';

    /**
     * Whether the Select is in an error state.
     */
    @Input() invalid = false;

    /**
     * The error message to display when the Select is in an error state.
     */
    @Input() error: string | string[] = '';

    /**
     * Whether to show a searchbar in the dropdown for multiple selects.
     */
    @Input() search = false;

    /**
     * A function to compare the selected value with the option value.
     */
    @Input() compareWith: (o1: any, o2: any) => boolean = (o1, o2) => {
        if (typeof o1 === 'object' && typeof o2 === 'object') {
            return JSON.stringify(o1) === JSON.stringify(o2);
        }

        return o1 === o2;
    };

    @Input() selectAllText = 'Select all';
    @Input() searchPlaceholder = 'Search...';
    @Input() hint?: string;
    @Input() floatLabel = false;

    @Output()
    change: EventEmitter<Event> = new EventEmitter<Event>();

    @ContentChildren(OptionComponent)
    optionComponents!: QueryList<OptionComponent>;

    @ViewChild('input') input!: ElementRef;

    @HostListener('document:mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen = false;
        }
    }

    @HostListener('focusout', ['$event'])
    onFocusOut(event: FocusEvent): void {
        const focusedInside = this.el.nativeElement.contains(event.target);
        if (!focusedInside) {
            this.isOpen = false;
        }
    }

    @HostListener('click')
    @HostListener('mouseenter')
    checkDropdownPosition() {
        const selectBox = this.el.nativeElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        this.isDropdownUpwards = selectBox.bottom + 200 > viewportHeight;
    }

    touched = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected selectedItems: [string, any][] = [];
    protected isOpen = false;
    protected focused = false;
    protected isDropdownUpwards = false;

    private subscriptions: Subscription[] = [];

    constructor(@Inject(THEME_TOKEN) private readonly theme: Theme, private el: ElementRef) {}

    public ngAfterContentInit(): void {
        this.setupSubscriptions();
        this.subscribeToOptionChanges();
    }

    public ngAfterViewInit(): void {
        // Prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.initSelectedItems();
        }, 0);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['value']) {
            this.initSelectedItems();
        }
    }

    public ngOnDestroy(): void {
        this.resetSubscriptions();
    }

    private resetSubscriptions(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    private setupSubscriptions(): void {
        this.optionComponents.forEach(option => {
            option.multiple = this.multiple;

            this.subscriptions.push(
                option.select.subscribe(data => {
                    this.handleOptionSelect(data);
                })
            );
        });
    }

    private handleOptionSelect(data: [string, any]): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const [label, value] = data as [string, any];

        this.input.nativeElement.focus();
        this.markAsTouched();

        if (this.multiple) {
            const index = this.selectedItems.findIndex(item => this.compareWith(item[1], value));

            if (index >= 0) {
                this.selectedItems.splice(index, 1);
            } else {
                this.selectedItems.push([label, value]);
            }

            this.writeValue(this.selectedItems.map(item => item[1]));
        } else {
            this.selectedItems = [[label, value]];

            this.writeValue(this.selectedItems[0] ? this.selectedItems[0][1] : undefined);
            this.isOpen = false;
            setTimeout(() => {
                this.input.nativeElement.blur();
            }, 50);
        }

        this.updateSelectedOptions();
    }

    protected togglePopover(event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        if (this.isOpen) {
            this.isOpen = false;
            this.input.nativeElement.blur();
        } else {
            this.isOpen = true;
            this.input.nativeElement.focus();
        }
    }

    protected get popoverClasses() {
        const popoverStyles = componentStylesByTheme(this.theme, selectStylesSAG, selectStylesSHS);

        return popoverStyles();
    }

    protected get searchClasses() {
        const searchStyles = componentStylesByTheme(this.theme, selectSearchStylesSAG, selectSearchStylesSHS);

        return searchStyles();
    }

    protected get searchInputClasses() {
        const searchInputStyles = componentStylesByTheme(
            this.theme,
            selectSearchInputStylesSAG,
            selectSearchInputStylesSHS
        );

        return searchInputStyles();
    }

    private initSelectedItems(): void {
        this.updateSelectedOptions();

        if (this.optionComponents) {
            this.selectedItems = this.optionComponents
                .filter(option => option.selected)
                .map(option => [option.text.nativeElement.innerText, option.value]);
        }
    }

    private updateSelectedOptions() {
        this.optionComponents?.forEach(option => {
            if (this.multiple) {
                option.selected = this.value?.some((val: any) => this.compareWith(val, option.value)) ?? false;
            } else {
                option.selected = this.compareWith(this.value, option.value);
            }
        });
    }

    protected get displayValue(): string {
        return this.selectedItems.map(item => item[0]).join(', ');
    }

    protected onFocus() {
        if (this.disabled || this.readOnly) return;

        this.focused = true;
        this.isOpen = true;
    }

    protected onBlur() {
        this.focused = false;
        setTimeout(() => {
            this.markAsTouched();
        }, 100);
    }

    // eslint-disable-next-line
    onChangeEvent = (_: any) => {};
    // eslint-disable-next-line
    onTouch = () => {};

    // eslint-disable-next-line
    registerOnChange(fn: any): void {
        this.onChangeEvent = fn;
    }

    // eslint-disable-next-line
    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    writeValue(val: any) {
        this.value = val;
        this.onChangeEvent(this.value);
        this.change.emit(this.value);

        try {
            this.initSelectedItems();
        } catch (e) {
            console.error(e);
        }
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    // eslint-disable-next-line
    validate(control: AbstractControl<any, any>): ValidationErrors | null {
        return this.required && !this.value ? { required: true } : null;
    }

    blockChange(event: Event) {
        event.preventDefault();

        // Force the input value to always match the display value
        if (event.target) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const target = event.target as any;

            // Use setTimeout to ensure this runs after the browser's default handling
            setTimeout(() => {
                target.value = this.displayValue;
            }, 0);
        }
    }

    isArray(value: string | string[]): value is string[] {
        return Array.isArray(value);
    }

    public get allSelected(): boolean {
        return this.optionComponents?.length === this.selectedItems.length;
    }

    public handleSelectAll(): void {
        this.isOpen = true;

        if (this.allSelected) {
            this.selectedItems = [];
            this.writeValue([]);
        } else {
            this.selectedItems = this.optionComponents.map(option => [
                option.text.nativeElement.innerText,
                option.value
            ]);
            this.writeValue(this.optionComponents.map(option => option.value));
        }
    }

    public handleSearch(event: Event): void {
        event.stopPropagation();

        const input = event.target as HTMLInputElement;
        const value = input.value;

        this.optionComponents.forEach(option => {
            const text = option.text.nativeElement.innerText;
            option.hidden = !text.toLowerCase().includes(value.toLowerCase());
        });
    }

    private randomId(): string {
        return Math.random().toString(36).substring(2);
    }

    private subscribeToOptionChanges(): void {
        this.optionComponents.changes
            .pipe(
                tap(() => {
                    this.resetSubscriptions();
                    this.setupSubscriptions();
                    this.initSelectedItems();
                })
            )
            .subscribe();
    }
}
